import { ITag, ITagLimit, ITagRaw } from '@models/Tag.model';
import { CategoryColor } from '@models/TagCategory.model';
import { getDefaultTagColor } from '@utils/getDefaultTagColor';
import { GroupableTags, TagGrp } from './TagDialog.types';

/** Assert that a specific catId belongs to the tagsPasssd, */
function hasCategory(
  tagGrp: Partial<TagGrp>,
  catId: keyof Partial<TagGrp>,
): tagGrp is TagGrp {
  return typeof tagGrp[catId] !== 'undefined';
}

/**
 * DO Tags.groupBy('category');
 */
export function groupTags(
  tags: GroupableTags[],
): readonly [grpMap: TagGrp, categories: string[]] {
  const grp: Partial<TagGrp> = {}; // partial because no keys are inside.
  const categories = new Set<string>();
  tags.forEach(tag => {
    const { tagCategoryName, tagCategoryId } = tag;
    if (tagCategoryName === 'Uncategorized') {
      // Find another uncategorized if it exists, and use.
      const cFound = Object.entries(grp).find(
        ([, v]) => v?.categoryName === tagCategoryName,
      );
      if (cFound) {
        const [tcId] = cFound;
        const tagGrp = grp[tcId] as TagGrp[string];
        tagGrp.tags.push(tag);
        return;
      }
    }
    if (!hasCategory(grp, tagCategoryId)) {
      const name = tagCategoryName ?? 'Uncategorized';
      const tcId = tagCategoryId;

      grp[tcId] = {
        tags: [tag],
        categoryName: name,
      };
      categories.add(tagCategoryId);
      return;
    }
    grp[tagCategoryId].tags.push(tag);
  });
  //   grp SHOULD have all keys in categories.
  return [grp as TagGrp, Array.from(categories)] as const;
}
function isITagRaw(tag: PossibleTagsVal[number]): tag is ITagRaw {
  return (
    'id' in tag &&
    'name' in tag &&
    'color' in tag &&
    'tcId' in tag &&
    'tcName' in tag
  );
}
function getDefaultTagCategoryName(catName?: string) {
  return catName || 'Uncategorized';
}
type GroupTagsArgs = Parameters<typeof groupTags>;
export type PossibleTagsVal = ITagLimit[] | ITagRaw[] | ITag[];

/**
 * Acts as tag compat between ITag and ITagRaw.
 * Note: This is also used to format RelatedTagsList tags
 */
export function toGroupTagsArgs(tags: PossibleTagsVal): GroupTagsArgs {
  const a0 = tags.map<GroupableTags>(each => {
    let color: CategoryColor;
    let tcId: string;
    let tcName: string;
    if (isITagRaw(each)) {
      color = getDefaultTagColor(each.color) as CategoryColor;
      tcId = each.tcId;
      tcName = getDefaultTagCategoryName(each.tcName);
    } else {
      color = getDefaultTagColor(each.tagCategory?.color) as CategoryColor;
      tcId = each.tagCategoryId;
      tcName = getDefaultTagCategoryName(each.tagCategory?.name);
    }
    return {
      id: each.id,
      color,
      tagName: each.name,
      tagCategoryId: tcId,
      tagCategoryName: tcName,
    };
  });

  return [a0];
}
export const DIALOG_OPEN_DELAY = 350;
